"use client";

import React from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface PricingTier {
  icon: string;
  title: string;
  price: string;
  description: string;
  features: string[];
  highlight?: boolean;
}

interface AddOnService {
  name: string;
  price: string;
}

const PricingSection: React.FC = () => {
  const sectionRef = useScrollAnimation<HTMLElement>();

  const pricingTiers: PricingTier[] = [
    {
      icon: "📋",
      title: "Initial Home Assessment",
      price: "UGX 100,000 - 150,000",
      description: "One-time mandatory assessment before starting care",
      features: [
        "Full nursing assessment",
        "Care plan development",
        "Risk evaluation",
        "Family briefing",
      ],
    },
    {
      icon: "🕒",
      title: "Hourly Bedside Care",
      price: "UGX 15,000 - 40,000/hr",
      description: "Short visits for medication, vitals, wound care",
      features: [
        "Medication administration",
        "Vitals check",
        "Wound care",
        "Minimum booking: 2 hours",
      ],
    },
    {
      icon: "🌙",
      title: "12-Hour Shift",
      price: "UGX 180,000 - 250,000",
      description: "Day or night shift for elderly, post-op, or palliative support",
      features: [
        "Continuous 12-hour care",
        "Day or night shift available",
        "Post-operative support",
        "Night shifts slightly higher",
      ],
      highlight: true,
    },
    {
      icon: "🛏",
      title: "24-Hour Care",
      price: "UGX 360,000 - 450,000",
      description: "Continuous care with rotational nurses",
      features: [
        "Round-the-clock coverage",
        "Staff rotation included",
        "No single nurse overwork",
        "Comprehensive monitoring",
      ],
    },
    {
      icon: "📅",
      title: "Weekly Package",
      price: "UGX 2.3M - 2.8M/week",
      description: "Long-term care packages (12-hour daily care)",
      features: [
        "12-hour daily care",
        "Weekly: UGX 2.3M - 2.8M",
        "Monthly: UGX 5.5M - 6.5M",
        "Better value for long-term cases",
      ],
      highlight: true,
    },
  ];

  const addOnServices: AddOnService[] = [
    { name: "Complex wound dressing", price: "UGX 30,000 - 60,000/session" },
    { name: "NG tube care", price: "UGX 30,000" },
    { name: "Catheter care/change", price: "UGX 50,000 - 80,000" },
    { name: "End-of-life/palliative care", price: "Premium tier pricing" },
  ];

  return (
    <section ref={sectionRef} className="relative py-12 md:py-16 lg:py-20 px-3 md:px-4 bg-gradient-to-b from-white via-blue-50/30 to-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12 lg:mb-16 reveal-up stagger-1">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 px-2">
            Home Care <span className="gold-text">Pricing Plans</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Transparent, flexible pricing for professional home nursing care in Kampala.
            Choose the plan that fits your needs.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6 mb-8 md:mb-10 lg:mb-12">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`pricing-card reveal-up stagger-${(index % 6) + 2} relative bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 shadow-lg border-2 ${
                tier.highlight
                  ? "border-gold ring-4 ring-gold/20 highlight" : "border-gray-100 hover:border-gold/40"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-gold-light text-navy px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              {/* Icon */}
              <div className="text-4xl md:text-5xl mb-3 md:mb-4">{tier.icon}</div>

              {/* Title */}
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                {tier.title}
              </h3>

              {/* Price */}
              <div className="text-2xl md:text-3xl font-bold text-gold mb-2 md:mb-3">
                {tier.price}
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-4 md:mb-6 text-xs md:text-sm leading-relaxed">
                {tier.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 md:space-y-3">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-xs md:text-sm text-gray-700">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 text-gold mr-2 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Specialized Services Add-Ons */}
        <div className="reveal-up stagger-3 bg-gradient-to-r from-gold/10 to-gold-light/10 rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 border border-gold/30">
          <div className="flex items-start md:items-center mb-4 md:mb-6">
            <span className="text-3xl md:text-4xl mr-3 md:mr-4 flex-shrink-0">🧴</span>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                Specialized Services (Add-Ons)
              </h3>
              <p className="text-gray-600 text-xs md:text-sm mt-1">
                Additional specialized care services available with any package
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {addOnServices.map((service, index) => (
              <div
                key={index}
                className={`addon-card reveal-up stagger-${index + 4} bg-white rounded-lg md:rounded-xl p-4 md:p-5 shadow-sm border border-gold/20 hover:border-gold/40`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
                      {service.name}
                    </h4>
                    <p className="text-gold font-bold text-base md:text-lg">
                      {service.price}
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-gold flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-8 md:mt-10 lg:mt-12 px-4 reveal-up stagger-2">
          <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
            Need a custom care plan? Contact us for personalized pricing.
          </p>
          <a
            href="https://wa.me/256788217719?text=Hello%2C%20I%27d%20like%20to%20inquire%20about%20your%20home%20care%20pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 font-semibold rounded-full shadow-lg hover:shadow-xl text-sm md:text-base"
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6 mr-2"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <span className="whitespace-nowrap">Get Custom Quote on WhatsApp</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;